import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { EmptyState } from "@/components/shared";
import { fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const PRIORITY_TONE = {
  Essential: "bg-destructive/15 text-destructive",
  Action: "bg-terracotta/15 text-terracotta",
  High: "bg-warning/25 text-[hsl(30_88%_30%)]",
  Medium: "bg-muted text-muted-foreground",
};

const Row = ({ label, value, strong, hint, onOverride, overridden }) => (
  <div className={`flex items-center justify-between gap-4 px-6 py-2.5 ${
    strong ? "bg-muted/40" : ""}`}>
    <div className="min-w-0">
      <p className={`text-sm ${strong ? "font-semibold text-navy" : "text-foreground/85"}`}>{label}</p>
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
    <div className="flex items-center gap-3">
      {overridden && (
        <span className="rounded-full bg-terracotta/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-terracotta">
          overwritten
        </span>
      )}
      <p className={`font-mono tabular-nums ${strong ? "text-base font-semibold text-navy" : "text-sm text-navy"}`}>
        {inr(value)}
      </p>
      {onOverride && (
        <button onClick={onOverride} className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground hover:text-terracotta">
          edit
        </button>
      )}
    </div>
  </div>
);

const PerfTable = ({ title, note, lines, testid }) => (
  <div className="panel overflow-hidden" data-testid={testid}>
    <div className="border-b border-border px-6 py-4">
      <p className="label-caps">{title}</p>
      {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-xs">
        <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          <tr>
            <th className="px-6 py-2.5 text-left">Line</th>
            <th className="px-4 py-2.5 text-right">Revenue</th>
            <th className="px-4 py-2.5 text-right">PVC</th>
            <th className="px-4 py-2.5 text-right">DVC</th>
            <th className="px-4 py-2.5 text-right">Throughput</th>
            <th className="px-4 py-2.5 text-right">Fixed expense</th>
            <th className="px-4 py-2.5 text-right">Coverage</th>
            <th className="px-4 py-2.5 text-right">Goal</th>
            <th className="px-4 py-2.5 text-right">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {lines.map((l) => (
            <tr key={l.label} data-testid={`${testid}-row-${l.label.replace(/\W+/g, "-")}`}
                className={l.kind === "common" || l.kind === "other" ? "bg-muted/20" : ""}>
              <td className="px-6 py-2.5 text-navy">
                <p className="text-xs font-medium">{l.label}</p>
                {l.flag && <p className="text-[10px] text-[hsl(30_88%_28%)]">{l.flag}</p>}
              </td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-navy">{inr(l.revenue)}</td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-navy">{inr(l.pvc)}</td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-navy">{inr(l.dvc)}</td>
              <td className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">{inr(l.throughput)}</td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-navy">{l.fixed ? inr(l.fixed) : "—"}</td>
              <td className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">
                {l.ratio === null || l.ratio === undefined ? "—" : `${l.ratio}×`}
              </td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
                {l.ratio === null || l.ratio === undefined ? "—" : `${l.goal}×`}
              </td>
              <td className="px-4 py-2.5 text-right">
                {l.status === "met" && (
                  <span className="rounded-full bg-success/20 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-success">On goal</span>)}
                {l.status === "below" && (
                  <span className="rounded-full bg-destructive/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-destructive">Below goal</span>)}
                {!l.status && <span className="text-muted-foreground">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/** Throughput generated vs fixed expense incurred — per production category and per outlet.
 *  The client's coverage goal (throughput ÷ fixed expense) is checked line by line. */
const Performance = ({ performance }) => {
  const cats = performance?.categories || [];
  const outs = performance?.outlets || [];
  if (!cats.length && !outs.length) return null;
  const company = performance.company || {};
  return (
    <div className="space-y-6" data-testid="fp-output-performance">
      <PerfTable title="Throughput vs fixed expense — production categories"
                 note="Revenue − PVC − DVC against the fixed cost mapped to each category. The distribution leg is charged on the register's actual outlet × category sales mix; company-common distribution cost follows revenue share. HUB stays unallocated by choice; welfare sits at function level, so this view covers Operation only."
                 lines={cats} testid="fp-perf-categories" />
      <PerfTable title="Throughput vs fixed expense — outlets"
                 note="The same statement per branch: each outlet is charged every category's PVC rate on what it actually sold. The lines add up — outlets plus non-outlet revenue minus net FG stock tie to the Level 1 throughput."
                 lines={outs} testid="fp-perf-outlets" />
      <p className="text-[11px] text-muted-foreground" data-testid="fp-perf-tie-note">
        Other (non-outlet) revenue {inr(company.other_revenue)} · Net FG stock {inr(company.net_fg_stock)} ·
        coverage goal {performance.goal}× — set under Setup → Performance goal.
      </p>
    </div>
  );
};

const Split = ({ title, map, testid }) => {
  const entries = Object.entries(map || {});
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (!entries.length) return null;
  return (
    <div className="panel overflow-hidden" data-testid={testid}>
      <div className="border-b border-border px-6 py-3">
        <p className="label-caps">{title}</p>
      </div>
      <div className="divide-y divide-border">
        {entries.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} className="px-6 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-navy">{k}</p>
              <p className="font-mono text-sm tabular-nums text-navy">{inr(v)}</p>
            </div>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-terracotta"
                   style={{ width: `${total ? (v / total) * 100 : 0}%` }} />
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 bg-muted/40 px-6 py-2.5">
          <p className="text-sm font-semibold text-navy">Total</p>
          <p className="font-mono text-sm font-semibold tabular-nums text-navy">{inr(total)}</p>
        </div>
      </div>
    </div>
  );
};

/** Month-on-month statement: the full cash-flow chain and the capital position, one column per
 *  month of the financial year with the cumulative column at the end. Balance-sheet lines always
 *  show the closing position of that month. */
const FCF_ROWS = [
  ["Revenue", "revenue"], ["Less: variable cost", "variable_cost"],
  ["Throughput / contribution", "throughput", true], ["Less: fixed cost", "fixed_expenses"],
  ["EBITDA", "ebitda", true], ["Less: interest", "interest"],
  ["Profit before tax", "pbt"], ["Less: tax", "tax"], ["Profit after tax", "pat", true],
  ["Less: working capital increase", "increase_in_working_capital"],
  ["Operating cash flow", "operating_cash_flow", true], ["Less: maintenance capex", "maintenance_capex"],
  ["Free cash flow", "free_cash_flow", true], ["Less: growth capex", "growth_capex"],
  ["Less: principal repayment", "principal_repayment"], ["Less: target dividend", "target_dividend"],
  ["Less: target cash reserve", "target_cash_reserve"], ["Surplus / (deficit) for the owners", "surplus_deficit", true],
];

const BS_ROWS = [
  ["Total debt", (b, sc) => b?.debt_closing, "money"],
  ["Equity / net worth", (b, sc) => b?.equity, "money"],
  ["Debt-equity ratio", (b, sc) => (b?.equity ? b.debt_closing / b.equity : null), "ratio"],
  ["Current assets", (b, sc) => sc?.["Current Assets"], "money"],
  ["Current liabilities", (b, sc) => sc?.["Current Liabilities"], "money"],
  ["Current ratio", (b, sc) => (sc?.["Current Liabilities"]
      ? sc["Current Assets"] / sc["Current Liabilities"] : null), "ratio"],
  ["Net working capital", (b, sc) =>
      (sc?.["Current Assets"] ?? null) !== null && (sc?.["Current Liabilities"] ?? null) !== null
        ? sc["Current Assets"] - sc["Current Liabilities"] : null, "money"],
];

const StatementMoM = ({ tenantId, period }) => {
  const { ctx } = useFpContext();
  const [cols, setCols] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!tenantId || !period) return;
      const y = parseInt(period.slice(0, 4), 10), mo = parseInt(period.slice(5, 7), 10);
      const fyStart = mo >= 4 ? y : y - 1;
      const months = (ctx?.periods || [])
        .filter((p) => {
          const py = parseInt(p.slice(0, 4), 10), pm = parseInt(p.slice(5, 7), 10);
          return (pm >= 4 ? py : py - 1) === fyStart && p <= period;
        })
        .sort();
      const res = await Promise.all([
        ...months.map((p) => api.get(`${fpBase(tenantId)}/outputs`, { params: { period: p, basis: "month" } })),
        api.get(`${fpBase(tenantId)}/outputs`, { params: { period, basis: "ytd" } }),
      ]);
      if (!alive) return;
      setCols([...months.map((p, i) => ({ key: p, label: monthLabel(p), data: res[i].data })),
               { key: "ytd", label: "Cumulative (YTD)", data: res[months.length].data }]);
    };
    load().catch(() => alive && setCols([]));
    return () => { alive = false; };
  }, [tenantId, period, ctx?.periods]);

  if (!cols?.length) return null;
  const fmt = (v, kind) => (v === null || v === undefined ? "—"
    : kind === "ratio" ? `${v.toFixed(2)}x` : inr(v));
  return (
    <div className="panel overflow-hidden" data-testid="fp-output-mom">
      <div className="border-b border-border px-6 py-4">
        <p className="label-caps">Month on month · cash flow and capital position</p>
        <h3 className="font-display text-lg text-navy">Every month of the year side by side</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Each column is one month; the last column accumulates April to the selected month.
          Balance-sheet lines always show the closing position of the month.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            <tr>
              <th className="px-5 py-2 text-left">Line</th>
              {cols.map((c) => (
                <th key={c.key} className={`px-5 py-2 text-right ${c.key === "ytd" ? "text-terracotta" : ""}`}
                    data-testid={`fp-mom-col-${c.key}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {FCF_ROWS.map(([label, key, strong]) => (
              <tr key={key} className={strong ? "bg-muted/40" : ""} data-testid={`fp-mom-${key}`}>
                <td className={`px-5 py-2 ${strong ? "font-semibold text-navy" : "text-foreground/85"}`}>
                  {label}
                </td>
                {cols.map((c) => (
                  <td key={c.key} className={`px-5 py-2 text-right font-mono tabular-nums ${
                    c.key === "ytd" ? "font-semibold text-navy" : "text-navy"}`}>
                    {fmt((c.data?.fcf || {})[key], "money")}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td colSpan={cols.length + 1}
                  className="bg-navy/5 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy">
                Capital &amp; liquidity
              </td>
            </tr>
            {BS_ROWS.map(([label, fn, kind]) => (
              <tr key={label} data-testid={`fp-mom-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <td className="px-5 py-2 text-foreground/85">{label}</td>
                {cols.map((c) => (
                  <td key={c.key} className="px-5 py-2 text-right font-mono tabular-nums text-navy">
                    {fmt(fn(c.data?.balance_sheet, c.data?.statements?.controls || {}), kind)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function FpOutputs() {
  const { tenantId, period, basis } = useFpContext();
  const [m, setM] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/outputs`, { params: { period, basis } });
      setM(data);
    } catch (e) { setM({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  const override = async (key, current) => {
    const entered = window.prompt(`Overwrite ${key.replace(/_/g, " ")}`, current ?? 0);
    if (entered === null) return;
    if (entered === "") {
      try {
        await api.delete(`${fpBase(tenantId)}/overrides/${key}`, { params: { period, basis } });
        toast.success("Override removed — back to the computed figure");
        load();
      } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    const reason = window.prompt("Why is it being overwritten? (kept in the audit trail)") || "";
    try {
      await api.put(`${fpBase(tenantId)}/overrides`, { metric_key: key, value: Number(entered), reason },
                    { params: { period, basis } });
      toast.success("Saved — the engine has recomputed around it");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ov = (key) => (m?.overrides || []).some((o) => o.metric_key === key);
  const l1 = m?.levels?.level1;
  const c = m?.levels?.consumption;
  const f = m?.fcf;

  return (
    <FpShell
      title="Output pack"
      description="Built only from classifications you have approved. Every figure can be overwritten, and the engine then recomputes around it — showing both your figure and the one it calculated."
    >
      {!period ? (
        <EmptyState testid="fp-outputs-empty" title="Nothing uploaded yet"
                    message="Upload a Profit & Loss, Balance Sheet or Trial Balance under Statements." />
      ) : m?.error ? (
        <EmptyState testid="fp-outputs-error" title="Unavailable" message={m.error} />
      ) : !m ? (
        <p className="label-caps animate-pulse">Building the output</p>
      ) : (
        <div className="space-y-6" data-testid="fp-outputs">
          {(m.data_requests || []).length > 0 && (
            <div className="panel overflow-hidden" data-testid="fp-data-requests">
              <div className="border-b border-border px-6 py-4">
                <p className="label-caps">What the engine still wants</p>
                <h3 className="font-display text-lg text-navy">
                  Nothing is mandatory — this is what would sharpen the output
                </h3>
              </div>
              <div className="divide-y divide-border">
                {m.data_requests.map((d) => (
                  <div key={d.key} className="flex flex-wrap items-start justify-between gap-3 px-6 py-3.5"
                       data-testid={`fp-request-${d.key}`}>
                    <div className="min-w-0 max-w-2xl">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${PRIORITY_TONE[d.priority] || "bg-muted"}`}>
                          {d.priority}
                        </span>
                        <p className="text-sm font-semibold text-navy">{d.title}</p>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">{d.why}</p>
                    </div>
                    <p className="text-[11px] text-terracotta">Unlocks: {d.unlocks}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(m.statements?.group_checks || []).length > 0 && (
            <div className="panel border-l-4 border-destructive p-6" data-testid="fp-group-checks">
              <p className="label-caps text-destructive">Does not tie to the statement</p>
              {m.statements.group_checks.map((g, i) => (
                <p key={i} className="mt-2 text-sm text-navy">
                  <b>{g.group}</b> — statement shows {inr(g.statement_total)}, classified lines add
                  up to {inr(g.classified_total)} ({inr(g.difference)} out). {g.issue}
                </p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="panel overflow-hidden" data-testid="fp-output-level1">
              <div className="border-b border-border px-6 py-4">
                <p className="label-caps">
                  Level 1 · {m.fiscal_year} · {basis === "ytd" ? "cumulative to" : ""} {monthLabel(period)}
                </p>
                <h3 className="font-display text-lg text-navy">Executive financial view</h3>
              </div>
              <div className="divide-y divide-border">
                <Row label="Revenue" value={l1.revenue} strong overridden={ov("revenue")}
                     onOverride={() => override("revenue", l1.revenue)} />
                <Row label="Less: production variable cost" value={l1.pvc} overridden={ov("pvc")}
                     hint={c?.total ? "Opening stock + purchases + direct expenses − closing stock − wastage" : ""}
                     onOverride={() => override("pvc", l1.pvc)} />
                <Row label="Less: distribution variable cost" value={l1.dvc} overridden={ov("dvc")}
                     hint={l1.dvc ? "" : "No selling & distribution group in the statement — any "
                                          + "freight or commission is still sitting inside variable cost"}
                     onOverride={() => override("dvc", l1.dvc)} />
                {l1.net_fg_stock ? (
                  <Row label="Less: net FG stock movement" value={l1.net_fg_stock}
                       hint="Traded & finished goods: opening stock + purchases − closing stock (client's COGS sheet)" />
                ) : null}
                <Row label="Throughput / contribution" value={l1.throughput} strong />
                <Row label="Less: fixed cost before interest" value={l1.fixed_expenses}
                     hint={l1.salary ? `Salary ${inr(l1.salary)} + other ${inr(l1.other_fixed)}` : ""}
                     overridden={ov("other_fixed")}
                     onOverride={() => override("other_fixed", l1.other_fixed)} />
                <Row label="Operating profit (before depreciation)" value={l1.ebitda} strong />
                <Row label="Less: depreciation" value={l1.depreciation} overridden={ov("depreciation")}
                     onOverride={() => override("depreciation", l1.depreciation)} />
                <Row label="Net operating profit after depreciation" value={l1.ebit} strong />
                <Row label="Other / non-operating income" value={l1.other_income} />
                <Row label="Less: interest" value={f.interest} overridden={ov("interest")}
                     onOverride={() => override("interest", f.interest)} />
                <Row label="PBT · profit before tax" value={f.pbt} />
                <Row label="Less: tax" value={f.tax} overridden={ov("tax")}
                     onOverride={() => override("tax", f.tax)} />
                <Row label="PAT · profit after tax" value={f.pat} strong />
              </div>
            </div>

            <div className="space-y-6">
              <div className="panel overflow-hidden" data-testid="fp-output-budget">
                <div className="border-b border-border px-6 py-4">
                  <p className="label-caps">Budget vs actual · {m.budget_vs_actual.source}</p>
                  <h3 className="font-display text-lg text-navy">Where we stand against plan</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    <tr>
                      <th className="px-5 py-2 text-left">Metric</th>
                      <th className="px-5 py-2 text-right">Actual</th>
                      <th className="px-5 py-2 text-right">% of sales</th>
                      <th className="px-5 py-2 text-right">Budget</th>
                      <th className="px-5 py-2 text-right">% of sales</th>
                      <th className="px-5 py-2 text-right">Deviation</th>
                      <th className="px-5 py-2 text-left"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {m.budget_vs_actual.rows.map((r) => (
                      <tr key={r.metric} data-testid={`fp-bva-${r.metric.replace(/\W+/g, "-")}`}>
                        <td className="px-5 py-2 text-navy">{r.metric}</td>
                        <td className="px-5 py-2 text-right font-mono tabular-nums">{inr(r.actual)}</td>
                        <td className="px-5 py-2 text-right font-mono text-xs tabular-nums text-navy">
                          {r.actual_pct_of_sales != null ? `${r.actual_pct_of_sales}%` : "—"}
                        </td>
                        <td className="px-5 py-2 text-right font-mono tabular-nums text-muted-foreground">
                          {inr(r.budget)}
                        </td>
                        <td className="px-5 py-2 text-right font-mono text-xs tabular-nums text-muted-foreground">
                          {r.budget_pct_of_sales != null ? `${r.budget_pct_of_sales}%` : "—"}
                        </td>
                        <td className={`px-5 py-2 text-right font-mono tabular-nums ${
                          r.status === "Adverse" ? "text-destructive" : "text-success"}`}>
                          {inr(r.variance)}{r.variance_pct !== null ? ` (${r.variance_pct}${r.pct_point ? " pp" : "%"})` : ""}
                        </td>
                        <td className="px-5 py-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                          {r.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {[["Production variable cost — block level", m.budget_vs_actual.pvc_detail, "fp-bva-pvc-detail"],
                  ["Distribution variable cost — budget lines", m.budget_vs_actual.dvc_detail, "fp-bva-dvc-detail"]]
                  .filter(([, rows]) => (rows || []).length > 0)
                  .map(([title, rows, tid]) => (
                    <div key={tid} className="border-t border-border px-6 py-4" data-testid={tid}>
                      <p className="label-caps">{title}</p>
                      {(() => {
                        const head = (m.budget_vs_actual.rows || [])
                          .find((x) => title.startsWith(x.metric)) || {};
                        const tot = rows.reduce((a, r) => ({
                          actual: a.actual + (r.actual || 0),
                          budget: a.budget + (r.budget || 0),
                        }), { actual: 0, budget: 0 });
                        tot.variance = Math.round((tot.actual - tot.budget) * 100) / 100;
                        // the % columns read from the headline row so the block total ties to
                        // Plan vs Performance to the exact hundredth of a point
                        tot.aps = head.actual_pct_of_sales;
                        tot.bps = head.budget_pct_of_sales;
                        tot.pp = head.variance_pct;
                        return (
                      <table className="mt-2 w-full text-sm">
                        <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                          <tr>
                            <th className="px-5 py-2 text-left font-bold">Line</th>
                            <th className="px-5 py-2 text-right font-bold whitespace-nowrap">Actual</th>
                            <th className="px-5 py-2 text-right font-bold whitespace-nowrap">% of sales</th>
                            <th className="px-5 py-2 text-right font-bold whitespace-nowrap">Budget</th>
                            <th className="px-5 py-2 text-right font-bold whitespace-nowrap">% of sales</th>
                            <th className="px-5 py-2 text-right font-bold whitespace-nowrap">Deviation</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {rows.map((r) => (
                            <tr key={r.line} data-testid={`${tid}-${r.line.replace(/\W+/g, "-")}`}>
                              <td className="px-5 py-2 text-xs text-navy">{r.line}</td>
                              <td className="px-5 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap">{inr(r.actual)}</td>
                              <td className="px-5 py-2 text-right font-mono text-xs tabular-nums text-navy whitespace-nowrap">
                                {r.actual_pct_of_sales != null ? `${r.actual_pct_of_sales}%` : "—"}
                              </td>
                              <td className="px-5 py-2 text-right font-mono text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                                {inr(r.budget)}
                              </td>
                              <td className="px-5 py-2 text-right font-mono text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                                {r.budget_pct_of_sales != null ? `${r.budget_pct_of_sales}%` : "—"}
                              </td>
                              <td className={`px-5 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap ${
                                r.status === "Adverse" ? "text-destructive" : "text-success"}`}>
                                {inr(r.variance)}{r.variance_pct !== null ? ` (${r.variance_pct}${r.pct_point ? " pp" : "%"})` : ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-navy/25 font-semibold"
                              data-testid={`${tid}-total`}>
                            <td className="px-5 py-2 text-xs text-navy">Total — {title.split(" — ")[0]}</td>
                            <td className="px-5 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap">{inr(tot.actual)}</td>
                            <td className="px-5 py-2 text-right font-mono text-xs tabular-nums text-navy whitespace-nowrap">{tot.aps}%</td>
                            <td className="px-5 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap">{inr(tot.budget)}</td>
                            <td className="px-5 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap">{tot.bps}%</td>
                            <td className={`px-5 py-2 text-right font-mono text-xs tabular-nums whitespace-nowrap ${
                              tot.actual > tot.budget + 0.005 ? "text-destructive" : "text-success"}`}>
                              {inr(tot.variance)} ({tot.pp > 0 ? "+" : ""}{tot.pp} pp)
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                        );
                      })()}
                    </div>
                  ))}
                {!m.budget_vs_actual.supplied && (
                  <p className="border-t border-border bg-warning/10 px-6 py-3 text-[11px] text-[hsl(30_88%_28%)]">
                    No budget yet. Upload one as a “Budget statement” under Statements in the same
                    line format as your P&amp;L, or type the targets on the Budgets tab.
                  </p>
                )}
              </div>

              <div className="panel overflow-hidden" data-testid="fp-output-consumption">
                <div className="border-b border-border px-6 py-4">
                  <p className="label-caps">Material consumed · stock &amp; wastage</p>
                </div>
                <div className="divide-y divide-border">
                  <Row label="Opening stock" value={c.opening_stock} hint={m.stock.opening_source}
                       overridden={ov("opening_stock")}
                       onOverride={() => override("opening_stock", c.opening_stock)} />
                  <Row label="Purchases" value={c.purchases} />
                  <Row label="Direct expenses" value={c.direct_expenses} />
                  <Row label="Less: closing stock" value={c.closing_stock} hint={m.stock.closing_source}
                       overridden={ov("closing_stock")}
                       onOverride={() => override("closing_stock", c.closing_stock)} />
                  <Row label="Less: wastage" value={c.wastage} overridden={ov("wastage")}
                       onOverride={() => override("wastage", c.wastage)} />
                  <Row label="Material consumed" value={c.total} strong />
                </div>
              </div>

              <div className="panel overflow-hidden" data-testid="fp-output-bs">
                <div className="border-b border-border px-6 py-4">
                  <p className="label-caps">Balance sheet position · closing</p>
                </div>
                <div className="divide-y divide-border">
                  {[["Receivables", "receivables"], ["Inventory", "inventory"],
                    ["Payables", "payables"], ["Debt", "debt_closing"], ["Equity", "equity"],
                    ["Cash", "cash"], ["Fixed assets", "fixed_assets"]].map(([lbl, key]) => (
                    <Row key={key} label={lbl} value={m.balance_sheet[key]} overridden={ov(key)}
                         onOverride={OVERRIDABLE_BS.has(key)
                           ? () => override(key, m.balance_sheet[key]) : undefined} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Performance performance={m.levels?.level2?.performance} />

          <StatementMoM tenantId={tenantId} period={period} />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Split title="Revenue by product category" testid="fp-split-revenue"
                   map={m.levels.level2.product_category} />
            <Split title="Cost by product category" testid="fp-split-cost"
                   map={m.levels.level2.pvc_by_category} />
            <Split title="Closing stock by category" testid="fp-split-stock"
                   map={m.levels.level2.closing_stock_by_category} />
            <Split title="Wastage by location" testid="fp-split-wastage"
                   map={m.levels.level2.wastage_by_location} />
            <Split title="Fixed cost by category" testid="fp-split-fixed-category"
                   map={m.levels.level2.fixed_by_category} />
            <Split title="Variable cost by component" testid="fp-split-component"
                   map={m.levels.level2.variable_by_component} />
            <Split title="Fixed cost by business function" testid="fp-split-function"
                   map={m.levels.level2.fixed_by_function} />
            <Split title="Revenue by stream" testid="fp-split-stream"
                   map={m.levels.level2.revenue_stream} />
            <Split title="Revenue by channel" testid="fp-split-channel"
                   map={m.levels.level2.channel} />
          </div>

          {(m.overrides || []).length > 0 && (
            <div className="panel overflow-hidden" data-testid="fp-override-log">
              <div className="border-b border-border px-6 py-4">
                <p className="label-caps">Overwritten figures</p>
                <h3 className="font-display text-lg text-navy">
                  Your figure and what the engine calculated
                </h3>
              </div>
              <div className="divide-y divide-border">
                {m.overrides.map((o) => (
                  <div key={o.metric_key} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
                    <div>
                      <p className="text-sm font-medium text-navy">{o.metric_key.replace(/_/g, " ")}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {o.reason || "No reason recorded"} · {o.by}
                      </p>
                    </div>
                    <p className="font-mono text-xs tabular-nums text-navy">
                      {inr(o.value)}{" "}
                      <span className="text-muted-foreground line-through">{inr(o.computed_value)}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </FpShell>
  );
}

const OVERRIDABLE_BS = new Set(["receivables", "inventory", "payables", "debt_closing", "equity"]);
